import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackRentalSerial } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';

const RentalTrack = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [serial, setSerial] = useState(params.get('serial') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const search = useCallback(async (value) => {
    const q = String(value || '').trim();
    if (q.length < 2) {
      setError('Enter at least 2 characters');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await trackRentalSerial(q);
      setData(res.data);
      setParams({ serial: q });
    } catch (err) {
      setError(getErrorMessage(err, 'Could not track serial'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [setParams]);

  useEffect(() => {
    const q = params.get('serial');
    if (q) search(q);
  }, []);

  const current = data?.current;
  const product = data?.product;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700}>Track rental item</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter a serial number to see the product and who rented it.
      </Typography>

      <Box component="form" onSubmit={(e) => { e.preventDefault(); search(serial); }} sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Serial number"
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
        />
        <Button type="submit" variant="contained">Track</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <CircularProgress />}

      {data && !loading && (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Product</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {product?.name || 'Not found in product serials'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[product?.product_code, data.serial_number, product?.serial_status].filter(Boolean).join(' · ')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Currently rented by</Typography>
                  {current ? (
                    <>
                      <Typography variant="h6" fontWeight={700}>
                        {current.customer?.name || 'Customer'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {current.rental_number} · {current.start_date} → {current.end_date}
                      </Typography>
                      <Button size="small" sx={{ mt: 1 }} onClick={() => navigate(`/rentals/${current.rental_id}`)}>
                        Open rental
                      </Button>
                      {current.customer?.id && (
                        <Button size="small" onClick={() => navigate(`/rentals/customers/${current.customer.id}`)}>
                          Customer profile
                        </Button>
                      )}
                    </>
                  ) : (
                    <Typography variant="h6">Not on an active rental</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Rental history</Typography>
            {(data.history || []).length === 0 ? (
              <Typography color="text.secondary">No rental history for this serial.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rental</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.history.map((h, idx) => (
                    <TableRow key={`${h.rental_id}-${idx}`} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rentals/${h.rental_id}`)}>
                      <TableCell>{h.rental_number}</TableCell>
                      <TableCell>{h.customer?.name || '—'}</TableCell>
                      <TableCell>{h.product_name}</TableCell>
                      <TableCell>{h.start_date} → {h.end_date}</TableCell>
                      <TableCell>
                        <Chip size="small" color={h.is_current ? 'success' : 'default'} label={h.is_current ? 'Current' : h.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default RentalTrack;
